import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { useAlert } from '../contexts/AlertContext';
import { useDoubleConfirm } from '../contexts/AlertContext';

// Újrahasználható jelszómező.
const PasswordInput = ({ value, onChange, label, placeholder, show, setShow, disabled = false, required = true }) => (
  <div className="relative">
    {label && <label className="block text-lg font-semibold mb-3 text-gray-700 dark:text-gray-200">{label}</label>}
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full p-4 border-2 border-blue-300 dark:border-blue-500 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900 rounded-2xl text-lg shadow-sm transition-all pr-12 dark:bg-gray-700 dark:text-white ${
          disabled ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600 cursor-not-allowed text-gray-500 dark:text-gray-400' : 'bg-white'
        }`}
        required={required}
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 focus:outline-none"
      >
        {show ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        )}
      </button>
    </div>
  </div>
);

export default function Profile() {
  const { user, logout, updateUser } = useAuth();
  const showAlert = useAlert();
  const doubleConfirm = useDoubleConfirm();

  // Profiladatok szerkesztéséhez és mentéséhez használt állapotok.
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '' });
  const [profilePassword, setProfilePassword] = useState('');
  const [saving, setSaving] = useState(false);

  // Jelszócsere mezői, mentési állapota és a mezők láthatósága.
  const [passwordEditMode, setPasswordEditMode] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [showPw1, setShowPw1] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [showPw3, setShowPw3] = useState(false);
  const [showProfilePassword, setShowProfilePassword] = useState(false);

  // Fióktörlés megerősítése és a hozzá tartozó jelszó bekérése.
  const [deleteDeleting, setDeleteDeleting] = useState(false);
  const [deletePasswordPrompt, setDeletePasswordPrompt] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [showDeletePassword, setShowDeletePassword] = useState(false);

  const [loading, setLoading] = useState(true);

  // A profilablak bezárásakor a jelszót is töröljük az űrlapból.
  const closeProfileModal = () => {
    setEditMode(false);
    setProfilePassword('');
  };

  const closePasswordModal = () => {
    setPasswordEditMode(false);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  // Bejelentkezett felhasználó adatainak betöltése.
  const loadProfileData = async () => {
    if (!user?.id) return;
    try {
      const profileRes = await axios.get(`/api/users/profile?userId=${user.id}`);
      setFormData({
        name: profileRes.data.name || '',
        phone: profileRes.data.phone || '',
        email: profileRes.data.email || ''
      });
    } catch (error) {
      console.error('Profil hiba:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfileData();
  }, [user]);

  // Módosított adatokat elküldése jelenlegi jelszóval.
  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`/api/users/profile`, {
        ...formData,
        userId: user.id,
        password: profilePassword
      });
      updateUser({ ...user, ...formData });
      showAlert('Profil sikeresen frissítve! ✅');
      closeProfileModal();
    } catch (error) {
      showAlert('Hiba: ' + (error.response?.data?.error || 'Sikertelen mentés'));
    }
    setSaving(false);
  };

  // Új jelszó és megerősítésnek ellenőrzése.
  const savePassword = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showAlert('A két új jelszó nem egyezik!');
      return;
    }
    setPasswordSaving(true);
    try {
      await axios.put(`/api/users/change-password`, {
        userId: user.id,
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      showAlert('Jelszó sikeresen módosítva! 🔒');
      closePasswordModal();
    } catch (error) {
      showAlert('Hiba: ' + (error.response?.data?.error || 'Sikertelen jelszócsere'));
    }
    setPasswordSaving(false);
  };

  // Kétlépcsős megerősítés után megnyitja a jelszóbekérő ablakot.
  const deleteAccount = async () => {
    if (!await doubleConfirm("Biztosan törölni szeretnéd a fiókodat? Ez a folyamat nem visszavonható!", "Minden adatod és jövőbeli foglalásod törlésre kerül. Valóban folytatod?")) return;

    setDeletePasswordPrompt(true);
  };

  // A jelszó ellenőrzése után törli a fiókot, majd kijelentkezteti a felhasználót.
  const confirmDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteDeleting(true);
    try {
      await axios.delete(`/api/users/profile?userId=${user.id}`, {
        data: { password: deletePassword }
      });
      showAlert("A fiókod sikeresen törölve lett. Viszlát! 👋");
      logout();
    } catch (error) {
      showAlert("Hiba a fiók törlése során: " + (error.response?.data?.error || "Sikertelen művelet"));
    } finally {
      setDeleteDeleting(false);
      setDeletePassword('');
      setDeletePasswordPrompt(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 dark:border-blue-900 dark:border-t-blue-400"></div>
    </div>
  );

  // A profiladatok megjelenítése és a szerkesztési lehetőségek.
  return (
    <div className="min-h-screen bg-transparent text-gray-900 dark:text-white transition-colors duration-300">
      <div className="max-w-6xl mx-auto p-4 sm:p-8 transition-colors duration-300">
        <div className="text-center mb-10 sm:mb-16">
          <div className="w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full mx-auto mb-6 sm:mb-8 shadow-2xl flex items-center justify-center">
            <span className="text-4xl sm:text-5xl">👤</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent mb-4 transition-colors break-words">
            Helló, {formData.name.split(' ')[0] || 'Vendég'}!
          </h1>
        </div>

        <div className="max-w-6xl mx-auto mb-12">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12">
            <div className="bg-slate-100/5 dark:bg-slate-800/60 rounded-xl p-5 sm:p-10 shadow-lg border border-slate-200/80 dark:border-slate-700/80 transition-colors duration-300">
              <div className="flex flex-wrap justify-between items-center gap-3 mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white transition-colors">Adatok</h2>
                <button
                  onClick={() => setEditMode(true)}
                  className="px-4 sm:px-6 py-3 font-bold rounded-xl shadow-lg transition-all text-xs sm:text-sm bg-blue-500 hover:bg-blue-600 text-white"
                >
                  ✏️ Szerkesztés
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-lg font-semibold mb-3 text-gray-700 dark:text-gray-300">👤 Név</label>
                  <div className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-2xl text-lg font-semibold bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200">{formData.name || 'Nincs megadva'}</div>
                </div>
                <div>
                  <label className="block text-lg font-semibold mb-3 text-gray-700 dark:text-gray-300">📧 Email</label>
                  <div className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-2xl text-lg font-semibold bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200">{formData.email || 'Nincs megadva'}</div>
                </div>
                <div>
                  <label className="block text-lg font-semibold mb-3 text-gray-700 dark:text-gray-300">📱 Telefon</label>
                  <div className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-2xl text-lg font-semibold bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200">{formData.phone || 'Nincs megadva'}</div>
                </div>
              </div>

            </div>

            <div className="flex flex-col gap-20">
            <div className="bg-slate-100/5 dark:bg-slate-800/60 rounded-xl p-5 sm:p-10 shadow-lg border border-slate-200/80 dark:border-slate-700/80 transition-colors duration-300">
              <div className="flex flex-wrap justify-between items-center gap-3 mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white transition-colors">Jelszó</h2>
                <button
                  onClick={() => setPasswordEditMode(true)}
                  className="px-4 sm:px-6 py-3 font-bold rounded-xl shadow-lg transition-all text-xs sm:text-sm bg-gray-700 hover:bg-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 text-white"
                >
                  🔑 Módosítás
                </button>
              </div>

              <p className="text-gray-500 dark:text-gray-400 text-lg transition-colors">A jelszavad biztonságban van.</p>
            </div>

            <div className="flex-1 flex flex-col justify-center bg-red-50/50 dark:bg-red-950/30 backdrop-blur-xl rounded-xl p-8 border border-red-200 dark:border-red-900/50 transition-colors duration-300 text-center">
              <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-2">Fiók Törlése</h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
                A fiókod törlésével minden személyes adatod véglegesen törlődik a rendszerből. Foglalásaid anonimizálva lesznek.
              </p>
              <button
                onClick={deleteAccount}
                disabled={deleteDeleting}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-8 rounded-2xl shadow-lg transition-all disabled:opacity-50 text-lg hover:scale-[1.02]"
              >
                {deleteDeleting ? '⏳ Törlés folyamatban...' : '🗑️ Profil végleges törlése'}
              </button>
            </div>
            </div>
          </div>
        </div>

        {/* Profiladatok szerkesztés ablak. */}
        {editMode && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center bg-gray-950/60 p-4 backdrop-blur-sm" onMouseDown={(e) => e.target === e.currentTarget && closeProfileModal()}>
            <form onSubmit={saveProfile} className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[1rem] border border-blue-200 bg-white p-6 sm:p-8 shadow-2xl dark:border-blue-900 dark:bg-gray-800">
              <div className="flex items-center justify-between gap-4 mb-6">
                <h2 className="text-2xl font-black text-gray-900 dark:text-white">Adatok szerkesztése</h2>
                <button type="button" onClick={closeProfileModal} className="text-2xl text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white" aria-label="Bezárás">×</button>
              </div>
              <div className="space-y-6">
                <div>
                  <label className="block text-lg font-semibold mb-3 text-gray-700 dark:text-gray-300">👤 Név</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full p-4 border-2 border-blue-300 dark:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900 rounded-2xl text-lg font-semibold dark:bg-gray-700 dark:text-white" required />
                </div>
                <div>
                  <label className="block text-lg font-semibold mb-3 text-gray-700 dark:text-gray-300">📧 Email</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full p-4 border-2 border-blue-300 dark:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900 rounded-2xl text-lg font-semibold dark:bg-gray-700 dark:text-white" required />
                </div>
                <div>
                  <label className="block text-lg font-semibold mb-3 text-gray-700 dark:text-gray-300">📱 Telefon</label>
                  <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full p-4 border-2 border-blue-300 dark:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900 rounded-2xl text-lg font-semibold dark:bg-gray-700 dark:text-white" />
                </div>
                <PasswordInput value={profilePassword} onChange={(e) => setProfilePassword(e.target.value)} label="🔒 Jelenlegi jelszó a mentéshez" placeholder="Írd be a jelenlegi jelszavad" show={showProfilePassword} setShow={setShowProfilePassword} />
              </div>
              <div className="mt-7 flex justify-end gap-3">
                <button type="button" onClick={closeProfileModal} className="rounded-xl bg-gray-100 px-5 py-3 font-bold text-gray-600 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">Mégse</button>
                <button type="submit" disabled={saving} className="rounded-xl bg-emerald-500 px-5 py-3 font-bold text-white shadow-lg transition-colors hover:bg-emerald-600 disabled:opacity-50">{saving ? '💾 Menti...' : '💾 Mentés'}</button>
              </div>
            </form>
          </div>
        )}

        {/* A jelenlegi jelszó ellenőrzése és az új jelszó megadása. */}
        {passwordEditMode && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center bg-gray-950/60 p-4 backdrop-blur-sm" onMouseDown={(e) => e.target === e.currentTarget && closePasswordModal()}>
            <form onSubmit={savePassword} className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[1rem] border border-gray-300 bg-white p-6 sm:p-8 shadow-2xl dark:border-gray-600 dark:bg-gray-800">
              <div className="flex items-center justify-between gap-4 mb-6">
                <h2 className="text-2xl font-black text-gray-900 dark:text-white">Jelszó módosítása</h2>
                <button type="button" onClick={closePasswordModal} className="text-2xl text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white" aria-label="Bezárás">×</button>
              </div>
              <div className="space-y-6">
                <PasswordInput value={passwordData.currentPassword} onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })} label="Jelenlegi jelszó" show={showPw1} setShow={setShowPw1} />
                <PasswordInput value={passwordData.newPassword} onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })} label="Új jelszó" show={showPw2} setShow={setShowPw2} />
                <PasswordInput value={passwordData.confirmPassword} onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} label="Új jelszó megerősítése" show={showPw3} setShow={setShowPw3} />
              </div>
              <div className="mt-7 flex justify-end gap-3">
                <button type="button" onClick={closePasswordModal} className="rounded-xl bg-gray-100 px-5 py-3 font-bold text-gray-600 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">Mégse</button>
                <button type="submit" disabled={passwordSaving} className="rounded-xl bg-gray-800 px-5 py-3 font-bold text-white shadow-lg transition-colors hover:bg-gray-900 disabled:opacity-50">{passwordSaving ? '⏳ Folyamatban...' : '🔑 Mentés'}</button>
              </div>
            </form>
          </div>
        )}

        {/* A fiók végleges törlése előtt bekéri a jelenlegi jelszót. */}
        {deletePasswordPrompt && (
          <div className="fixed inset-0 z-[210] flex items-center justify-center bg-gray-950/60 p-4 backdrop-blur-sm">
            <form
              onSubmit={confirmDeleteAccount}
              className="w-full max-w-md rounded-[2rem] border border-red-200 bg-white p-7 shadow-2xl dark:border-red-900 dark:bg-gray-800"
            >
              <h2 className="mb-3 text-xl font-black text-gray-900 dark:text-white">Jelszó szükséges</h2>
              <p className="mb-6 text-sm font-semibold leading-6 text-gray-600 dark:text-gray-300">
                A fiók törlésének megerősítéséhez írd be a jelenlegi jelszavad.
              </p>
              <PasswordInput
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                label="Jelenlegi jelszó"
                placeholder="Írd be a jelenlegi jelszavad"
                show={showDeletePassword}
                setShow={setShowDeletePassword}
              />
              <div className="mt-7 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setDeletePasswordPrompt(false);
                    setDeletePassword('');
                  }}
                  className="rounded-xl bg-gray-100 px-5 py-3 text-sm font-black text-gray-600 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  disabled={deleteDeleting}
                  className="rounded-xl bg-red-600 px-5 py-3 text-sm font-black text-white shadow-lg transition-colors hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteDeleting ? 'Törlés...' : 'Fiók törlése'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}